/**
 * appointmentWindow.js — the ONE definition of "can this patient check in yet".
 *
 * v242.69.0 (CASEHOME-APPT-ACTIONS-1). Before this file, CaseHome carried two
 * independent copies of the same rule: one inside the agentTemporal memo that
 * feeds the Ask Orella opener, one inside the encounter-1 chat block that picks
 * the dialogue line. Both read `hoursUntil <= 24 && hoursUntil > -2`. Adding the
 * action row would have made a third, and three copies of a time window drift
 * the moment anyone tunes one of them — the chat would invite a check-in the
 * button refuses, or the button would open a check-in the chat says is closed.
 *
 * The window is asymmetric on purpose: it OPENS 24h before the appointment and
 * stays open until 2h AFTER it, so a patient running late can still check in.
 *
 * Status gate: a case must actually be with a doctor. 'checked_in' stays in the
 * list because the screen still has to render the row for someone who already
 * checked in — eligibility is about the window, not about whether they used it.
 */

import { isDeclined, requestCancelledByPhysician } from '../../utils/declinedRequest.js' // PROD-983, PROD-1137 (import-free owner)
import { hasPendingReschedule, confirmedCheckInSlot } from '../../utils/pendingReschedule.js' // PROD-1007 (import-free owner)
import { wallTimeToInstant } from '../../utils/dateUtils.js' // PROD-1368: the slot is LA wall time, not device time

export const CHECK_IN_OPENS_HOURS_BEFORE = 24
export const CHECK_IN_CLOSES_HOURS_AFTER = 2

/**
 * PROD-1369 (R1368 N2): THE window boundary, shared by appointmentWindow, VisitsScreen and DiagnoseScreen.
 * Open from 24 h before (inclusive) until 2 h after (exclusive): at exactly +2 h (hoursUntil === -2) it is closed.
 */
export function isWithinCheckInWindow(hoursUntil) {
  return hoursUntil <= CHECK_IN_OPENS_HOURS_BEFORE && hoursUntil > -CHECK_IN_CLOSES_HOURS_AFTER
}

export const CHECK_IN_STATUSES = ['scheduled', 'claimed', 'in_progress', 'checked_in']

/**
 * The confirmed slot wins over the requested one — a reschedule must not reopen the old window.
 * PROD-1368: the slot is the practice's wall clock (ORELLA_TZ), so it becomes a true instant through
 * dateUtils.wallTimeToInstant, never `new Date(y, mo - 1, d, h, m)` (that reads it in the DEVICE zone,
 * and a patient in New York saw the window open three hours late).
 */
export function appointmentDateTime(apptReq) {
  const date = apptReq?.confirmedDate || apptReq?.requestedDate || ''
  const time = apptReq?.confirmedTime || apptReq?.requestedTime || ''
  if (!date || !time) return null
  return wallTimeToInstant(date, time)
}

/** Hours from `now` (a Date or epoch ms; default Date.now()) until the appointment. Negative means it already started. null = no slot. */
export function hoursUntilAppointment(apptReq, now = Date.now()) {
  const dt = appointmentDateTime(apptReq)
  if (!dt) return null
  return (dt.getTime() - Number(now)) / (1000 * 60 * 60)
}

/**
 * `caseItem` (optional, PROD-1007): the case. While it holds a reschedule request
 * (pending_reschedule) the request's NEW time opens no check-in; only the kept old slot
 * (confirmedCheckInSlot, the held status_history entry of PROD-1008) does, and with none the case is not eligible.
 */
export function isCheckInEligible(status, apptReq, now = Date.now(), caseItem = null) {
  if (!CHECK_IN_STATUSES.includes(status)) return false
  // PROD-983: a request the physician declined is no visit; its time opens no check-in.
  if (isDeclined(apptReq?.status)) return false
  // PROD-1137: a visit the physician cancelled is no visit either; its old time opens no check-in.
  if (requestCancelledByPhysician(apptReq)) return false
  let slotSource = apptReq
  if (hasPendingReschedule(caseItem)) {
    const slot = confirmedCheckInSlot(caseItem)
    if (!slot) return false
    slotSource = { confirmedDate: slot.date, confirmedTime: slot.time }
  }
  const hoursUntil = hoursUntilAppointment(slotSource, now)
  if (hoursUntil === null) return false
  return isWithinCheckInWindow(hoursUntil)
}
